import { useTranslation } from 'react-i18next';
import { FunctionComponent } from 'react';
import { Button, Flex, Form, FormInstance, InputNumber } from 'antd';

import { isRTL } from 'core/services/language';

import Icon from 'utils/icon';
import Riyal from 'components/riyal/riyal';

interface PriceComponentProps {
	form: FormInstance;
	fieldName: string;
	inputWidth?: number;
	viewOnly?: boolean;
	transparent?: boolean;
}

const PriceComponent: FunctionComponent<PriceComponentProps> = ({ form, fieldName, inputWidth = 180, viewOnly, transparent }) => {
	const { t } = useTranslation();

	const fieldWatcher = Form.useWatch(fieldName, form);

	return (
		<Flex
			gap={10}
			align="center"
			style={{
				minHeight: 40,
				paddingInline: 10,
				border: '1px solid rgb(217, 217, 217)',
				borderRadius: 'var(--radius)',
				width: 'max-content',
				background: transparent ? 'transparent' : '#fff',
				borderColor: transparent ? 'transparent' : 'rgb(217, 217, 217)',
			}}>
			{viewOnly ? (
				<div style={{ width: 28, height: 28 }} />
			) : (
				<Button
					type="primary"
					className="h-max"
					style={{ padding: 7 }}
					onClick={() => {
						const current = Number(fieldWatcher) || 0;
						form.setFieldsValue({ [fieldName]: current - 1 });
					}}
					disabled={fieldWatcher === 0}>
					<Icon name="minus" color="#fff" width={12} height={12} />
				</Button>
			)}

			<Form.Item
				name={fieldName}
				className="mb-0"
				rules={[
					{ required: true, message: t('COMMON.FIELD_REQUIRED') },
					{
						validator: (_, value) => {
							if (value === null || value === undefined || value <= 0) {
								return Promise.reject(new Error(t('COMMON.FIELD_REQUIRED')));
							}
							return Promise.resolve();
						},
					},
				]}>
				<InputNumber<number>
					variant="filled"
					min={0}
					className={`transparent borderless p-0 d-flex align-items-center ${isRTL() ? 'text-end' : 'text-start'}`}
					step={1}
					precision={2}
					formatter={(value) => {
						if (value === undefined || value === null) return '';
						const num = typeof value === 'string' ? parseFloat((value as string).replace(/,/g, '')) : value;
						if (Number.isNaN(num)) return '';
						return num.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						});
					}}
					parser={(displayValue) => {
						if (!displayValue) return 0;
						const raw = displayValue.replace(/,/g, '').replace(/\s/g, '');
						const n = parseFloat(raw);
						if (Number.isNaN(n)) return 0;
						return Math.round(n * 100) / 100;
					}}
					addonBefore={<Riyal size={16} />}
					style={{ width: inputWidth, minHeight: 40, direction: 'ltr', textAlign: 'center' }}
				/>
			</Form.Item>

			{viewOnly ? (
				<div style={{ width: 28, height: 28 }} />
			) : (
				<Button
					type="primary"
					className="h-max"
					style={{ padding: 7 }}
					onClick={() => {
						const current = Number(fieldWatcher) || 0;
						form.setFieldsValue({ [fieldName]: current + 1 });
					}}>
					<Icon name="plus" color="#fff" width={12} height={12} />
				</Button>
			)}
		</Flex>
	);
};

export default PriceComponent;
